import classNames from "classnames"
import React from "react"

import { Color } from "../../../../constants/V2/color"
import Badge from "../../../elements/V2/Badge"
import BulletItem from "../../../elements/V2/BulletItem"
import PillButton from "../../../elements/V2/Buttons/PillButton"
import TextButton from "../../../elements/V2/Buttons/TextButton"
import GatsbyStoryblokImage from "../../../elements/V2/GatsbyStoryblokImage"
import NumberDisplay from "../../../elements/V2/NumberDisplay"
import Typography from "../../../elements/V2/Typography"
import LayoutItem from "../../Layouts/LayoutItem"

import AddToCartButton from "@components/elements/V2/Buttons/AddToCartButton"
import { PurchasableItem } from "@hooks/V2/storyblok"
import { useScrollAnimateSelf } from "@hooks/V2/useScrollAnimate"
import { getBackgroundColorClass, getAccentColor } from "@utils/V2/color"

type Props = {
  backgroundColor: Color
  accentColor: Color
  imageUrl?: string
  imageAlt?: string
  heading: string
  description: string
  priceValue: string | number
  priceSuperscriptText?: string
  priceAdornmentText: string
  priceStrikeSuperscriptText: boolean
  priceDisclaimerText?: string
  priceSubscriptText?: string
  priceSideScriptText?: string
  pillButtonText: string
  pillButtonUrl: string
  textButtonText?: string
  textButtonUrl?: string
  disclaimerText?: string
  badgeText?: string
  bundleIncludesTitle?: string
  buyerRecievesList?: React.ComponentProps<typeof BulletItem>[]
  bundleIncludesList?: React.ComponentProps<typeof BulletItem>[]
  adornmentTextPosition: "left" | "right"
  purchasable?: boolean
  purchasableItem?: PurchasableItem
  reasonNotAvailable?: string
  waitlistLink?: string
  mobileGridOrder?: number
} & Partial<Storyblok.CTATrackingEvent>

const PackagesMaster = ({
  backgroundColor,
  accentColor,
  imageUrl,
  imageAlt,
  heading,
  description,
  priceValue,
  priceSuperscriptText,
  priceAdornmentText,
  priceStrikeSuperscriptText,
  priceSubscriptText,
  priceDisclaimerText,
  pillButtonText,
  pillButtonUrl,
  textButtonText,
  textButtonUrl,
  disclaimerText,
  badgeText,
  buyerRecievesList,
  bundleIncludesTitle = "Bundle Includes",
  bundleIncludesList,
  adornmentTextPosition,
  priceSideScriptText,
  purchasable = false,
  purchasableItem,
  reasonNotAvailable,
  waitlistLink,
  mobileGridOrder,
  trackingEvent,
  trackingEventKey,
  trackingEventValue,
  ...props
}: Props) => {
  const { animationRefCallback } = useScrollAnimateSelf(true)

  return (
    <LayoutItem
      mobileGridOrder={mobileGridOrder}
      as="div"
      ref={animationRefCallback}
      className={classNames(
        getBackgroundColorClass(backgroundColor),
        "rounded-lg col-span-4 flex flex-col justify-between gap-20 px-20 pt-24 sm:col-span-2 md:col-span-3 md:gap-24 md:px-24 md:pt-32 lg:col-span-6 lg:gap-32 lg:px-32 lg:pt-48"
      )}
      {...props}
    >
      <div className="flex flex-grow flex-col gap-20 md:gap-24 lg:gap-32">
        {badgeText ? (
          <Badge
            text={badgeText}
            textColor={getAccentColor(backgroundColor)}
            accentColor={getAccentColor(backgroundColor)}
            style="bordered"
            className="mb-4 self-end"
          />
        ) : (
          <div className="mb-32"></div>
        )}

        <Typography
          font="grotesk"
          weight="medium"
          size="h3"
          color={getAccentColor(backgroundColor)}
          text={heading}
          element="h3"
        />

        <Typography
          font="grotesk"
          weight="book"
          size="body-lg"
          color={getAccentColor(backgroundColor)}
          text={description}
        />

        <div>
          <NumberDisplay
            type="price"
            size="large"
            value={priceValue}
            superscriptText={priceSuperscriptText}
            adornmentText={priceAdornmentText}
            strikeSuperscriptText={priceStrikeSuperscriptText}
            adornmentTextPosition={adornmentTextPosition}
            textColor={getAccentColor(backgroundColor)}
            subscriptText={priceSubscriptText}
            sideScriptText={priceSideScriptText}
          />

          {priceDisclaimerText && (
            <Typography
              font="grotesk"
              weight="book"
              size="subscript-sm"
              color={getAccentColor(backgroundColor)}
              text={priceDisclaimerText}
            />
          )}
        </div>

        {buyerRecievesList && (
          <div className="flex flex-col gap-16">
            <Typography
              font="grotesk"
              weight="medium"
              size="body-lg"
              color={getAccentColor(backgroundColor)}
              text="You get:"
            />

            <div className="flex flex-col gap-4">
              {buyerRecievesList.map((bulletItem, index) => (
                <BulletItem
                  key={`${bulletItem.text.replace(" ", "-")}-${index}`}
                  text={bulletItem.text}
                  answerText={bulletItem.answerText}
                  textColor={getAccentColor(backgroundColor)}
                  iconOverrideColor={getAccentColor(backgroundColor)}
                />
              ))}
            </div>
          </div>
        )}
        <div className="flex h-full items-end justify-start gap-20">
          {purchasable && purchasableItem ? (
            <AddToCartButton
              style="solid"
              purchasableItem={purchasableItem}
              buttonColor={Color.Blue}
              reasonNotAvailable={reasonNotAvailable}
              waitlistLink={waitlistLink}
            />
          ) : (
            <PillButton
              size="large"
              style="bordered"
              text={pillButtonText}
              linkUrl={pillButtonUrl}
              color={accentColor}
              overrideTextColor={getAccentColor(backgroundColor)}
              trackingEvent={trackingEvent}
              trackingEventKey={trackingEventKey}
              trackingEventValue={trackingEventValue}
            />
          )}

          {textButtonText && textButtonUrl && (
            <TextButton
              text={textButtonText}
              style="text"
              linkUrl={textButtonUrl}
              color={getAccentColor(backgroundColor)}
              trackingEvent={trackingEvent}
              trackingEventKey={trackingEventKey}
              trackingEventValue={trackingEventValue}
            />
          )}
        </div>
      </div>

      {disclaimerText && (
        <Typography
          font="grotesk"
          weight="book"
          size="subscript-sm"
          color={getAccentColor(backgroundColor)}
          text={disclaimerText}
        />
      )}
      <div className="grid grid-cols-1 items-end sm:grid-cols-2">
        {bundleIncludesList && (
          <div className="flex flex-col gap-16 self-center pb-20">
            <Typography
              font="grotesk"
              weight="medium"
              size="body-lg"
              color={getAccentColor(backgroundColor)}
              text={bundleIncludesTitle}
            />

            <div className="flex flex-col gap-4">
              {bundleIncludesList.map((bulletItem, index) => (
                <BulletItem
                  key={`${bulletItem.text.replace(" ", "-")}-${index}`}
                  text={bulletItem.text}
                  textColor={getAccentColor(backgroundColor)}
                  iconOverrideColor={getAccentColor(backgroundColor)}
                  size="body-md"
                />
              ))}
            </div>
          </div>
        )}

        {imageUrl && (
          <GatsbyStoryblokImage
            image={imageUrl}
            alt={imageAlt || heading}
            aspectRatio="4:3"
          />
        )}
      </div>
    </LayoutItem>
  )
}

export default PackagesMaster
